/**
 * Feedback modal — form with type selector, JPD idea typeahead,
 * customer segment dropdown, file upload with platform-specific
 * capture instructions, contact email, and text input.
 */

import type { JpdIdea } from "../types";
import { getIdeas, getSegments } from "../api/jpd-cache";

const CLOSE_ICON = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`;
const ATTACH_ICON = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>`;

const MAX_ATTACHMENT_BYTES = 250 * 1024 * 1024; // 250 MB (presigned S3 upload, bypasses API Gateway)
const TYPEAHEAD_DEBOUNCE_MS = 250;
const EMAIL_STORAGE_KEY = "ip-feedback-email";
const ALLOWED_ATTACHMENT_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "video/mp4",
  "video/webm",
  "video/quicktime",
]);

export interface ModalController {
  close: () => void;
  setSubmitting: (submitting: boolean) => void;
}

interface ModalOptions {
  jpdProject: string;
  apiUrl: string;
  onSubmit: (data: {
    summary: string;
    text: string;
    feedbackType: string;
    contactEmail: string | null;
    jpdIdeaKey: string | null;
    jpdIdeaSummary: string | null;
    customerSegment: string | null;
    attachmentBlob: Blob | null;
    attachmentContentType: string | null;
  }) => void;
  onCancel: () => void;
}

export function createModal(shadow: ShadowRoot, options: ModalOptions): ModalController {
  let feedbackType = "bug";
  let selectedIdea: JpdIdea | null = null;
  let segmentsGeneration = 0;
  let selectedSegment: string | null = null;
  let attachmentBlob: Blob | null = null;
  let attachmentContentType: string | null = null;
  let jpdInput: HTMLInputElement | null = null;
  let segmentSelect: HTMLSelectElement | null = null;
  let attachmentObjectUrl: string | null = null;
  const previousFocus: Element | null = document.activeElement;

  const overlay = document.createElement("div");
  overlay.className = "ip-overlay";
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) options.onCancel();
  });

  const modal = document.createElement("div");
  modal.className = "ip-modal";
  modal.setAttribute("role", "dialog");
  modal.setAttribute("aria-modal", "true");
  modal.setAttribute("aria-label", "Submit feedback");

  // ── Header
  const header = document.createElement("div");
  header.className = "ip-modal-header";

  const title = document.createElement("h2");
  title.className = "ip-modal-title";
  title.textContent = "Send Feedback";

  const closeBtn = document.createElement("button");
  closeBtn.className = "ip-close";
  closeBtn.innerHTML = CLOSE_ICON;
  closeBtn.setAttribute("aria-label", "Close feedback form");
  closeBtn.addEventListener("click", options.onCancel);

  header.appendChild(title);
  header.appendChild(closeBtn);
  modal.appendChild(header);

  // ── Type selector
  const typeGroup = document.createElement("div");
  typeGroup.className = "ip-type-group";
  typeGroup.setAttribute("role", "group");
  typeGroup.setAttribute("aria-label", "Feedback type");

  const types = [
    { value: "bug", label: "\uD83D\uDC1B Bug" },
    { value: "feedback", label: "\uD83D\uDCDD Feedback" },
    { value: "question", label: "\u2753 Question" },
  ];

  const typeButtons: HTMLButtonElement[] = [];
  for (const type of types) {
    const btn = document.createElement("button");
    btn.className = `ip-type-btn${type.value === feedbackType ? " active" : ""}`;
    btn.textContent = type.label;
    btn.setAttribute("aria-pressed", String(type.value === feedbackType));
    btn.addEventListener("click", () => {
      feedbackType = type.value;
      typeButtons.forEach((b, i) => {
        b.className = `ip-type-btn${types[i].value === feedbackType ? " active" : ""}`;
        b.setAttribute("aria-pressed", String(types[i].value === feedbackType));
      });
    });
    typeButtons.push(btn);
    typeGroup.appendChild(btn);
  }
  modal.appendChild(typeGroup);

  // ── Title input
  const titleGroup = document.createElement("div");
  titleGroup.className = "ip-form-group";

  const titleLabel = document.createElement("label");
  titleLabel.className = "ip-label";
  titleLabel.setAttribute("for", "ip-feedback-title");
  titleLabel.textContent = "\u270F\uFE0F Title";

  const titleInput = document.createElement("input");
  titleInput.className = "ip-input";
  titleInput.id = "ip-feedback-title";
  titleInput.type = "text";
  titleInput.placeholder = "Brief summary of the issue\u2026";
  titleInput.maxLength = 200;
  titleInput.required = true;

  titleGroup.appendChild(titleLabel);
  titleGroup.appendChild(titleInput);
  modal.appendChild(titleGroup);

  // ── Text input
  const formGroup = document.createElement("div");
  formGroup.className = "ip-form-group";

  const label = document.createElement("label");
  label.className = "ip-label";
  label.setAttribute("for", "ip-feedback-text");
  label.textContent = "\uD83D\uDCAC What happened?";

  const textarea = document.createElement("textarea");
  textarea.className = "ip-textarea";
  textarea.id = "ip-feedback-text";
  textarea.placeholder = "Describe the issue or share your thoughts...";
  textarea.maxLength = 5000;
  textarea.required = true;

  formGroup.appendChild(label);
  formGroup.appendChild(textarea);
  modal.appendChild(formGroup);

  // ── Customer Segment dropdown (shown immediately when jpdProject is set)
  let segmentGroup: HTMLDivElement | null = null;
  if (options.jpdProject) {
    segmentGroup = document.createElement("div");
    segmentGroup.className = "ip-form-group";
    // Hidden until segments are successfully fetched and non-empty
    segmentGroup.style.display = "none";

    const segLabel = document.createElement("label");
    segLabel.className = "ip-label";
    segLabel.setAttribute("for", "ip-feedback-segment");
    segLabel.textContent = "\uD83C\uDFAF Your role / segment";

    segmentSelect = document.createElement("select");
    segmentSelect.className = "ip-select";
    segmentSelect.id = "ip-feedback-segment";

    const defaultOpt = document.createElement("option");
    defaultOpt.value = "";
    defaultOpt.textContent = "Select your segment\u2026";
    segmentSelect.appendChild(defaultOpt);

    segmentSelect.addEventListener("change", () => {
      selectedSegment = segmentSelect!.value || null;
      // Re-search ideas filtered by this segment
      if (jpdInput && !selectedIdea) {
        triggerIdeaSearch(jpdInput.value.trim());
      }
    });

    segmentGroup.appendChild(segLabel);
    segmentGroup.appendChild(segmentSelect);
    modal.appendChild(segmentGroup);

    // Fetch segments immediately on modal open (deferred so fetchSegments is defined)
    queueMicrotask(() => fetchSegments());
  }

  // ── JPD idea typeahead (only if jpdProject is configured)
  let typeaheadInput: HTMLInputElement | null = null;
  // Shared idea search function — used by both typeahead input and segment change
  let triggerIdeaSearch = (_q: string) => {};
  if (options.jpdProject) {
    const jpdGroup = document.createElement("div");
    jpdGroup.className = "ip-form-group";

    const jpdLabel = document.createElement("label");
    jpdLabel.className = "ip-label";
    jpdLabel.setAttribute("for", "ip-feedback-jpd");
    jpdLabel.textContent = "\uD83D\uDCA1 Linked idea (optional)";
    jpdGroup.appendChild(jpdLabel);

    const typeahead = document.createElement("div");
    typeahead.className = "ip-typeahead";

    jpdInput = document.createElement("input");
    jpdInput.className = "ip-input";
    jpdInput.id = "ip-feedback-jpd";
    jpdInput.type = "text";
    jpdInput.placeholder = "Search ideas\u2026";
    jpdInput.setAttribute("autocomplete", "off");
    jpdInput.setAttribute("role", "combobox");
    jpdInput.setAttribute("aria-autocomplete", "list");
    jpdInput.setAttribute("aria-expanded", "false");
    typeaheadInput = jpdInput;

    const resultsList = document.createElement("ul");
    resultsList.className = "ip-typeahead-results hidden";
    resultsList.setAttribute("role", "listbox");

    // Selected idea display
    const selectedWrap = document.createElement("div");
    selectedWrap.className = "ip-typeahead-selected";
    selectedWrap.style.display = "none";

    const selectedText = document.createElement("span");
    selectedText.className = "ip-typeahead-selected-text";

    const clearBtn2 = document.createElement("button");
    clearBtn2.className = "ip-typeahead-clear";
    clearBtn2.type = "button";
    clearBtn2.setAttribute("aria-label", "Clear selected idea");
    clearBtn2.textContent = "\u00d7";
    clearBtn2.addEventListener("click", () => {
      selectedIdea = null;
      selectedWrap.style.display = "none";
      if (jpdInput) {
        jpdInput.style.display = "";
        jpdInput.value = "";
        jpdInput.focus();
      }
      // Re-search ideas (segment filter may still apply)
      triggerIdeaSearch("");
    });

    selectedWrap.appendChild(selectedText);
    selectedWrap.appendChild(clearBtn2);

    typeahead.appendChild(jpdInput);
    typeahead.appendChild(resultsList);
    typeahead.appendChild(selectedWrap);
    jpdGroup.appendChild(typeahead);
    modal.appendChild(jpdGroup);

    // Debounced search
    let searchTimer: ReturnType<typeof setTimeout> | null = null;
    let searchGeneration = 0;

    const renderResults = (ideas: JpdIdea[]) => {
      resultsList.innerHTML = "";
      if (ideas.length === 0) {
        const empty = document.createElement("li");
        empty.className = "ip-typeahead-empty";
        empty.textContent = "No matching ideas";
        resultsList.appendChild(empty);
        return;
      }
      for (const idea of ideas) {
        const li = document.createElement("li");
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "ip-typeahead-item";
        btn.setAttribute("role", "option");
        const keySpan = document.createElement("span");
        keySpan.className = "ip-typeahead-item-key";
        keySpan.textContent = idea.key;
        const summarySpan = document.createElement("span");
        summarySpan.textContent = idea.summary;
        btn.appendChild(keySpan);
        btn.appendChild(summarySpan);
        btn.addEventListener("click", () => {
          selectedIdea = idea;
          selectedText.textContent = `${idea.key} \u2014 ${idea.summary}`;
          selectedWrap.style.display = "flex";
          if (jpdInput) jpdInput.style.display = "none";
          resultsList.classList.add("hidden");
          jpdInput?.setAttribute("aria-expanded", "false");
        });
        li.appendChild(btn);
        resultsList.appendChild(li);
      }
    };

    const searchIdeas = async (query: string) => {
      const gen = ++searchGeneration;
      try {
        // Use session cache (prefetched on widget init) with local filtering.
        // Falls back to live API call if segment filter is active (server-side JQL)
        // or if cache isn't ready yet.
        const ideas = await getIdeas(options.apiUrl, options.jpdProject, query, selectedSegment);
        if (gen !== searchGeneration) return;
        renderResults(ideas);
        resultsList.classList.remove("hidden");
        jpdInput?.setAttribute("aria-expanded", "true");
      } catch (err) {
        if (gen !== searchGeneration) return;
        console.warn("IPFeedback: JPD idea search failed:", err);
        resultsList.classList.add("hidden");
        jpdInput?.setAttribute("aria-expanded", "false");
      }
    };

    triggerIdeaSearch = (q: string) => {
      if (searchTimer) clearTimeout(searchTimer);
      if (q.length === 0 && !selectedSegment) {
        resultsList.classList.add("hidden");
        jpdInput?.setAttribute("aria-expanded", "false");
        return;
      }
      searchTimer = setTimeout(() => searchIdeas(q), TYPEAHEAD_DEBOUNCE_MS);
    };

    jpdInput.addEventListener("input", () => {
      triggerIdeaSearch(jpdInput!.value.trim());
    });

    // Close results on blur (with delay for click to register)
    jpdInput.addEventListener("blur", () => {
      setTimeout(() => {
        resultsList.classList.add("hidden");
        jpdInput?.setAttribute("aria-expanded", "false");
      }, 200);
    });
  }

  const fetchSegments = async () => {
    if (!options.jpdProject || !segmentGroup || !segmentSelect) return;
    const gen = ++segmentsGeneration;
    try {
      // Read from session cache (prefetched on widget init)
      const segs = await getSegments(options.apiUrl, options.jpdProject);
      if (gen !== segmentsGeneration) return;
      if (segs.length === 0) {
        segmentGroup!.style.display = "none";
        return;
      }

      // Populate select (keep the default empty option)
      while (segmentSelect!.options.length > 1) {
        segmentSelect!.remove(1);
      }
      for (const seg of segs) {
        const opt = document.createElement("option");
        opt.value = seg.label;
        opt.textContent = seg.label;
        segmentSelect!.appendChild(opt);
      }
      segmentGroup!.style.display = "";
    } catch (err) {
      console.warn("IPFeedback: segment fetch failed:", err);
      segmentGroup!.style.display = "none";
    }
  };

  // ── File upload with platform-specific instructions
  const uploadGroup = document.createElement("div");
  uploadGroup.className = "ip-form-group";

  const uploadLabel = document.createElement("label");
  uploadLabel.className = "ip-label";
  uploadLabel.textContent = "\uD83D\uDCCE Attach a screenshot or recording (optional)";
  uploadGroup.appendChild(uploadLabel);

  // Platform-specific capture instructions
  const captureHint = document.createElement("div");
  captureHint.className = "ip-capture-hint";
  const isMac =
    navigator.platform?.toLowerCase().includes("mac") || navigator.userAgent?.includes("Mac");
  if (isMac) {
    captureHint.innerHTML =
      "<strong>Mac:</strong> <kbd>\u2318\u21e7 3</kbd> full screen \u00b7 " +
      "<kbd>\u2318\u21e7 4</kbd> selection \u00b7 " +
      "<kbd>\u2318\u21e7 5</kbd> screen recording";
  } else {
    captureHint.innerHTML =
      "<strong>Windows:</strong> <kbd>Win+Shift+S</kbd> Snipping Tool \u00b7 " +
      "<kbd>PrtSc</kbd> full screen \u00b7 " +
      "Xbox Game Bar for recordings";
  }
  uploadGroup.appendChild(captureHint);

  // File input
  const fileWrap = document.createElement("div");
  fileWrap.className = "ip-file-wrap";

  const fileBtn = document.createElement("button");
  fileBtn.type = "button";
  fileBtn.className = "ip-file-btn";
  fileBtn.innerHTML = `${ATTACH_ICON}<span>Choose file</span>`;

  const fileInput = document.createElement("input");
  fileInput.type = "file";
  fileInput.accept = Array.from(ALLOWED_ATTACHMENT_TYPES).join(",");
  fileInput.className = "ip-file-input";
  fileInput.setAttribute("aria-label", "Attach screenshot or recording");

  const fileError = document.createElement("div");
  fileError.className = "ip-file-error";
  fileError.style.display = "none";

  const fileName = document.createElement("span");
  fileName.className = "ip-file-name";
  fileName.textContent = "No file chosen";

  fileBtn.addEventListener("click", () => fileInput.click());

  fileInput.addEventListener("change", () => {
    fileError.style.display = "none";
    fileError.textContent = "";

    const file = fileInput.files?.[0];
    if (!file) {
      attachmentBlob = null;
      attachmentContentType = null;
      fileName.textContent = "No file chosen";
      return;
    }
    if (!ALLOWED_ATTACHMENT_TYPES.has(file.type)) {
      attachmentBlob = null;
      attachmentContentType = null;
      fileError.textContent = "Unsupported format. Allowed: JPG, PNG, WebP, GIF, MP4, WebM, MOV";
      fileError.style.display = "block";
      fileName.textContent = "No file chosen";
      fileInput.value = "";
      return;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      attachmentBlob = null;
      attachmentContentType = null;
      fileError.textContent = `File too large (${(file.size / 1024 / 1024).toFixed(1)}MB, max 250MB)`;
      fileError.style.display = "block";
      fileName.textContent = "No file chosen";
      fileInput.value = "";
      return;
    }
    attachmentBlob = file;
    attachmentContentType = file.type;
    if (attachmentObjectUrl) URL.revokeObjectURL(attachmentObjectUrl);
    attachmentObjectUrl = URL.createObjectURL(file);
    fileName.textContent = file.name;
    fileName.classList.remove("ip-file-error");
    fileBtn.querySelector("span")!.textContent = `Replace: ${file.name}`;
  });

  fileWrap.appendChild(fileBtn);
  fileWrap.appendChild(fileInput);
  fileWrap.appendChild(fileName);
  fileWrap.appendChild(fileError);
  uploadGroup.appendChild(fileWrap);
  modal.appendChild(uploadGroup);

  // ── Contact email
  const emailGroup = document.createElement("div");
  emailGroup.className = "ip-form-group";

  const emailLabel = document.createElement("label");
  emailLabel.className = "ip-label";
  emailLabel.setAttribute("for", "ip-feedback-email");
  emailLabel.textContent = "\u2709\uFE0F Contact email (optional)";

  const emailInput = document.createElement("input");
  emailInput.className = "ip-input";
  emailInput.id = "ip-feedback-email";
  emailInput.type = "email";
  emailInput.placeholder = "you@example.com";

  // Auto-populate from previous submission
  try {
    const saved = localStorage.getItem(EMAIL_STORAGE_KEY);
    if (saved) emailInput.value = saved;
  } catch {
    // localStorage may be unavailable (private browsing, iframe restrictions)
  }

  emailGroup.appendChild(emailLabel);
  emailGroup.appendChild(emailInput);
  modal.appendChild(emailGroup);

  // ── Disclaimer
  const disclaimer = document.createElement("p");
  disclaimer.className = "ip-disclaimer";
  disclaimer.textContent =
    "We record and review all feedback but may not be able to reply to every submission.";
  modal.appendChild(disclaimer);

  // ── Actions
  const actions = document.createElement("div");
  actions.className = "ip-actions";

  const cancelBtn = document.createElement("button");
  cancelBtn.className = "ip-btn ip-btn-cancel";
  cancelBtn.textContent = "Cancel";
  cancelBtn.addEventListener("click", options.onCancel);

  const submitBtn = document.createElement("button");
  submitBtn.className = "ip-btn ip-btn-submit";
  submitBtn.textContent = "Submit";
  submitBtn.addEventListener("click", () => {
    const summary = titleInput.value.trim();
    if (!summary) {
      titleInput.focus();
      return;
    }
    const text = textarea.value.trim();
    if (!text) {
      textarea.focus();
      return;
    }
    const email = emailInput.value.trim();
    // Remember email for future submissions
    try {
      if (email) localStorage.setItem(EMAIL_STORAGE_KEY, email);
      else localStorage.removeItem(EMAIL_STORAGE_KEY);
    } catch {
      // localStorage may be unavailable
    }
    options.onSubmit({
      summary,
      text,
      feedbackType,
      contactEmail: email || null,
      jpdIdeaKey: selectedIdea?.key ?? null,
      jpdIdeaSummary: selectedIdea?.summary ?? null,
      customerSegment: selectedSegment,
      attachmentBlob,
      attachmentContentType,
    });
  });

  actions.appendChild(cancelBtn);
  actions.appendChild(submitBtn);
  modal.appendChild(actions);

  overlay.appendChild(modal);
  shadow.appendChild(overlay);

  requestAnimationFrame(() => {
    overlay.classList.add("open");
    textarea.focus();
  });

  function handleKeydown(e: KeyboardEvent) {
    if (e.key === "Escape") {
      options.onCancel();
      return;
    }

    if (e.key === "Tab") {
      const focusable = modal.querySelectorAll<HTMLElement>(
        'button, input:not([type="file"]), textarea, select, [tabindex]:not([tabindex="-1"])',
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && shadow.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && shadow.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  overlay.addEventListener("keydown", handleKeydown);

  function close() {
    overlay.classList.remove("open");
    overlay.removeEventListener("keydown", handleKeydown);
    if (attachmentObjectUrl) {
      URL.revokeObjectURL(attachmentObjectUrl);
      attachmentObjectUrl = null;
    }
    setTimeout(() => overlay.remove(), 200);

    if (previousFocus instanceof HTMLElement) {
      previousFocus.focus();
    }
  }

  function setSubmitting(submitting: boolean) {
    submitBtn.disabled = submitting;
    submitBtn.innerHTML = submitting ? '<span class="ip-spinner"></span>Submitting...' : "Submit";
    cancelBtn.disabled = submitting;
    titleInput.disabled = submitting;
    textarea.disabled = submitting;
    emailInput.disabled = submitting;
    fileBtn.disabled = submitting;
    if (typeaheadInput) typeaheadInput.disabled = submitting;
    if (segmentSelect) segmentSelect.disabled = submitting;
  }

  return { close, setSubmitting };
}
